import { Music, Pause, Volume2 } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { AnimatePresence, motion } from "framer-motion";

export function MusicPlayer() {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(15);
  const [src, setSrc] = useState<string | null>(null);
  const [showTip, setShowTip] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const fetchMusic = async () => {
      const { data } = await supabase.storage.from("music").list("", {
        limit: 1,
        sortBy: { column: "created_at", order: "desc" },
      });
      if (data && data.length > 0) {
        const { data: urlData } = supabase.storage.from("music").getPublicUrl(data[0].name);
        setSrc(urlData.publicUrl);
      }
    };
    fetchMusic();
  }, []);

  useEffect(() => {
    if (!src) return;
    const key = "music-tip-shown";
    if (!localStorage.getItem(key)) {
      const timer = setTimeout(() => setShowTip(true), 800);
      localStorage.setItem(key, "1");
      return () => clearTimeout(timer);
    }
  }, [src]);

  // Tip stays until user dismisses it (no auto-hide timer)

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setPlaying(!playing);
    setShowTip(false);
  };

  if (!src) return null;

  return (
    <div className="relative flex items-center gap-0">
      <audio ref={audioRef} loop src={src} />

      <AnimatePresence>
        {showTip && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3 }}
            className="absolute top-full mt-3 -left-12 z-[100] whitespace-nowrap cursor-pointer"
            onClick={() => setShowTip(false)}
          >
            <div className="relative bg-primary text-primary-foreground text-sm font-fredoka px-4 py-3 rounded-xl shadow-lg">
              <div className="absolute -top-1.5 left-14 w-3 h-3 bg-primary rotate-45 rounded-sm" />
              🎵 Listen to my music here too!
              <span className="ml-2 text-primary-foreground/70 text-xs">✕</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        animate={showTip ? { y: [0, -3, 0] } : {}}
        transition={showTip ? { duration: 0.6, repeat: Infinity, repeatDelay: 0.8 } : {}}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          className="rounded-full text-foreground"
          aria-label={playing ? "Pause music" : "Play music"}
        >
          {playing ? <Pause className="h-5 w-5" /> : <Music className="h-5 w-5" />}
        </Button>
      </motion.div>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full text-foreground h-8 w-8" aria-label="Volume">
            <Volume2 className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-10 p-3" side="top" align="center">
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs text-muted-foreground">{volume}%</span>
            <Slider
              orientation="vertical"
              min={0}
              max={100}
              step={1}
              value={[volume]}
              onValueChange={([v]) => setVolume(v)}
              className="h-24"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
